import { Disposable } from 'using-statement';
import { BloombergMarketDataRequest } from 'marketData/MarketDataRequest';
import { Logger, LoggingService } from '@instinet-analytics/core-lib';
import { BBGMarketDataAPI } from '@glue42/bbg-market-data/dist/cjs/bbg-market-data-api';
import { Glue42Core } from '@glue42/core';
import { Request } from '@glue42/bbg-market-data/dist/cjs/request';
import { recordMessage, recordStatus } from '../store/bloombergSlice';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import { AnyAction as AnyReduxAction } from 'redux';

type RequestStatus = 'Not Started' | 'Running' | 'Error' | 'Complete';

export interface DapiRequestManagerBaseOptions {
    dispatcher: ActionsDispatcher;
    glueInteropApi: Glue42Core.Interop.API;
    loggingService: LoggingService;
    mktDataApi: BBGMarketDataAPI;
}

export abstract class DapiRequestManagerBase implements Disposable {
    protected readonly glueInteropApi: Glue42Core.Interop.API;
    protected readonly mktDataApi: BBGMarketDataAPI;
    protected readonly dispatcher: ActionsDispatcher;
    public status: RequestStatus = 'Not Started';

    protected constructor(
        public request: BloombergMarketDataRequest,
        protected logger: Logger,
        options: DapiRequestManagerBaseOptions
    ) {
        this.glueInteropApi = options.glueInteropApi;
        this.mktDataApi = options.mktDataApi;
        this.dispatcher = options.dispatcher;
    }

    public abstract subscribe(): void;

    public async unsubscribe() {
        return await this.closeRequest();
    }

    protected abstract closeRequest(): Promise<void>;

    public dispose() {
        (async () => await this.closeRequest())();
    }

    protected dispatch(...actions: AnyReduxAction[]) {
        this.dispatcher.dispatch(...actions);
    }
}

interface OpenRequestOptions<T> {
    logger: Logger;
    dispatcher: ActionsDispatcher;
    uid: string;
    requestType: string;
    onResponseData: (response: T) => void;
}
export function sendRequest<T>(
    request: Request<T>,
    options: OpenRequestOptions<T>
) {
    const { uid, requestType, onResponseData, logger, dispatcher } = options;
    request.onData((response) => {
        dispatcher.dispatch(
            recordMessage({ uid, requestType, eventType: 'Response', response })
        );
        onResponseData(response.data);
    });

    request.onError((err) => {
        logger.debug(`${requestType} error`, err);
        dispatcher.dispatch(
            recordMessage({
                uid,
                requestType,
                eventType: 'BloombergError',
                response: err,
            })
        );
    });

    request.onStatus((status) => {
        logger.debug(`${requestType} onStatus`, status);
        dispatcher.dispatch(
            recordStatus({ uid, requestType, eventType: 'Response', status })
        );
    });

    request
        .open()
        .then((r) => {
            logger.debug('Sent request', r);
        })
        .catch((err) => {
            logger.debug('Error sending request', err);
        });
}
